import { useEffect, useMemo, useState } from 'preact/hooks';
import { ApiError, api } from '../../lib/api.js';
import { useApi } from '../../hooks/useApi.js';
import { Button } from '../../components/Button.jsx';

const YELLOW = '#F4C430';
const CORE = ['ink', 'media', 'maintenance', 'parts', 'global'];
const GLOBALISH = new Set(['global']);

export const NewItemModal = ({ open, onClose, onCreated, defaultPrinterId, defaultCategory = 'ink' }) => {
  const { data: printers } = useApi('/printers');
  const { data: categoriesList } = useApi('/inventory/categories');
  const [name, setName] = useState('');
  const [category, setCategory] = useState(defaultCategory);
  const [customCategoryText, setCustomCategoryText] = useState('');
  const [customMode, setCustomMode] = useState(false);
  const [printerId, setPrinterId] = useState(defaultPrinterId ?? '');
  const [low, setLow] = useState('');
  const [med, setMed] = useState('');
  const [stockCount, setStockCount] = useState('0');
  const [barcode, setBarcode] = useState('');
  const [productId, setProductId] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) {
      setName('');
      setCategory(defaultCategory);
      setPrinterId(defaultPrinterId ?? '');
      setCustomCategoryText('');
      setCustomMode(!CORE.includes(defaultCategory));
      setLow(''); setMed(''); setStockCount('0');
      setBarcode(''); setProductId('');
      setError(null);
    }
  }, [open, defaultCategory, defaultPrinterId]);

  const extras = useMemo(() => {
    if (!categoriesList) return [];
    return categoriesList.filter((c) => !CORE.includes(c));
  }, [categoriesList]);

  if (!open) return null;

  const effectiveCategory = customMode ? customCategoryText.trim().toLowerCase() : category;
  const needsPrinter = !GLOBALISH.has(effectiveCategory);

  const save = async () => {
    if (!name.trim() || !effectiveCategory) return;
    setBusy(true);
    setError(null);
    try {
      const result = await api.post('/inventory', {
        name: name.trim(),
        category: effectiveCategory,
        printerId: !needsPrinter ? null : (printerId ? Number(printerId) : null),
        stockCount: parseInt(stockCount, 10) || 0,
        lowThreshold: low === '' ? null : parseInt(low, 10),
        medThreshold: med === '' ? null : parseInt(med, 10),
        barcode: barcode.trim() || null,
        productId: productId.trim() || null,
      });
      onCreated?.(result);
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      class="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/55 px-4 py-10"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div class="w-full max-w-md overflow-hidden rounded-xl border border-border-subtle bg-bg-base">
        <div class="border-b border-border-subtle bg-bg-header px-5 py-4">
          <p class="eyebrow mb-1" style={{ color: YELLOW }}>New inventory item</p>
        </div>

        <div class="space-y-4 p-5">
          <div>
            <p class="mb-1 text-[12px] font-semibold text-text-tertiary">Name</p>
            <input
              type="text"
              autoFocus
              value={name}
              onInput={(e) => setName(e.currentTarget.value)}
              placeholder="e.g. Cyan, Cleaning swabs, Gloves"
              class="block w-full rounded-md border border-border-strong bg-bg-card px-3 py-2 text-sm font-medium text-text-primary placeholder:text-text-muted focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
            />
          </div>

          <div>
            <div class="mb-1 flex items-center justify-between">
              <p class="m-0 text-[12px] font-semibold text-text-tertiary">Category</p>
              <button
                type="button"
                onClick={() => setCustomMode((v) => !v)}
                class="text-[11px] font-semibold text-brand hover:underline"
              >
                {customMode ? 'Use a preset' : '+ New category'}
              </button>
            </div>

            {!customMode ? (
              <div class="flex flex-wrap gap-1.5">
                {[...CORE, ...extras].map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setCategory(c)}
                    class={`rounded-full border px-3 py-1 text-[12px] font-semibold capitalize ${
                      category === c
                        ? 'border-ink-yellow bg-ink-yellow/[0.12] text-ink-yellow'
                        : 'border-border-strong text-text-tertiary hover:bg-bg-card'
                    }`}
                  >
                    {c}
                  </button>
                ))}
              </div>
            ) : (
              <>
                <input
                  type="text"
                  value={customCategoryText}
                  onInput={(e) => setCustomCategoryText(e.currentTarget.value)}
                  placeholder="e.g. ppe, office, electronics"
                  class="block w-full rounded-md border border-border-strong bg-bg-card px-3 py-2 text-sm font-medium text-text-primary placeholder:text-text-muted focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
                />
                <p class="mt-1 text-[11px] font-medium text-text-muted">
                  Stored as lowercase. Will become a new section in the inventory view.
                </p>
              </>
            )}
          </div>

          {needsPrinter && (
            <div>
              <p class="mb-1 text-[12px] font-semibold text-text-tertiary">Printer</p>
              <select
                value={printerId}
                onChange={(e) => setPrinterId(e.currentTarget.value)}
                class="block w-full rounded-md border border-border-strong bg-bg-card px-3 py-2 text-sm font-medium text-text-primary focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
              >
                <option value="">Select a printer (or leave blank for shared global)…</option>
                {(printers ?? []).map((p) => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </div>
          )}

          <div class="grid grid-cols-3 gap-3">
            <div>
              <p class="mb-1 text-[12px] font-semibold text-text-tertiary">Stock</p>
              <input type="number" min={0} value={stockCount} onInput={(e) => setStockCount(e.currentTarget.value)}
                class="block w-full rounded-md border border-border-strong bg-bg-card px-2 py-1.5 text-right font-serif text-[16px] font-medium text-text-primary focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand" />
            </div>
            <div>
              <p class="mb-1 text-[12px] font-semibold text-text-tertiary">Low ≤</p>
              <input type="number" min={0} value={low} onInput={(e) => setLow(e.currentTarget.value)}
                class="block w-full rounded-md border border-border-strong bg-bg-card px-2 py-1.5 text-right font-serif text-[16px] font-medium text-text-primary focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand" />
            </div>
            <div>
              <p class="mb-1 text-[12px] font-semibold text-text-tertiary">Med ≤</p>
              <input type="number" min={0} value={med} onInput={(e) => setMed(e.currentTarget.value)}
                class="block w-full rounded-md border border-border-strong bg-bg-card px-2 py-1.5 text-right font-serif text-[16px] font-medium text-text-primary focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand" />
            </div>
          </div>

          <div class="grid grid-cols-2 gap-3">
            <div>
              <p class="mb-1 text-[12px] font-semibold text-text-tertiary">Barcode</p>
              <input type="text" value={barcode} onInput={(e) => setBarcode(e.currentTarget.value)}
                class="block w-full rounded-md border border-border-strong bg-bg-card px-3 py-1.5 font-mono text-[13px] font-medium text-text-primary focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand" />
            </div>
            <div>
              <p class="mb-1 text-[12px] font-semibold text-text-tertiary">Product ID</p>
              <input type="text" value={productId} onInput={(e) => setProductId(e.currentTarget.value)}
                class="block w-full rounded-md border border-border-strong bg-bg-card px-3 py-1.5 font-mono text-[13px] font-medium text-text-primary focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand" />
            </div>
          </div>

          {error && (
            <p class="text-[13px] font-semibold text-ink-magenta">
              {error instanceof ApiError ? error.message : (error.message ?? 'Failed to create')}
            </p>
          )}
        </div>

        <div class="flex justify-end gap-2 border-t border-border-subtle bg-bg-header px-5 py-3">
          <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button
            variant="primary"
            onClick={save}
            disabled={busy || !name.trim() || !effectiveCategory || (customMode && !customCategoryText.trim())}
          >
            {busy ? 'Creating…' : 'Add item'}
          </Button>
        </div>
      </div>
    </div>
  );
};
